import React, { createContext, use } from "react";
import { Box, createShapeId, Editor, StateNode, TLUiToolItem } from "tldraw";

import { ColorVariant } from "@/components/canvas-editor/components/Container";
import { isLayerShape } from "@/components/canvas-editor/shapes/shapes";
import { FeedVersion } from "@/lib/types";

export const preventBubble = {
  onClick: (e: React.MouseEvent) => e.stopPropagation(),
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
  onTouchEnd: (e: React.TouchEvent) => e.stopPropagation(),
  onTouchCancel: (e: React.TouchEvent) => e.stopPropagation(),
  onTouchMove: (e: React.TouchEvent) => e.stopPropagation(),
  onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
  onKeyUp: (e: React.KeyboardEvent) => e.stopPropagation(),
};

export const MIN_H = 92;
export const feedId = createShapeId("feed");
export const previewId = createShapeId("preview");

export abstract class ToolStateNode extends StateNode {
  public static uiToolDef: (editor: Editor) => Omit<
    TLUiToolItem<string, string>,
    "icon"
  > & {
    docs: React.ComponentType<React.SVGAttributes<SVGElement>>;
    icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
    kbd?: string;
    defaultColor: ColorVariant;
  };
}

export const ReadonlyEditorContext = createContext(false);
export function useIsReadonlyEditor() {
  return use(ReadonlyEditorContext);
}

export const CustomNodeEditorContext = createContext(false);

/**
 * Which component the custom-node editor is editing, when it is editing one.
 *
 * `CustomNodeEditorContext` only says that we are in the node editor; the
 * picker needs the id, so that a node cannot be added to itself. Undefined on
 * the create route, where there is no id yet and nothing can reference it.
 */
export const EditingCustomNodeIdContext = createContext<number | undefined>(
  undefined,
);

export function compact<T>(arr: T[]): NonNullable<T>[] {
  return arr.filter((i) => i !== undefined && i !== null);
}

export function centerContent(
  editor: Editor,
  { accountForSidebar }: { accountForSidebar?: boolean } = {},
) {
  // The layers beside the Feed are part of what a creator opens the canvas to
  // see, so the first view fits them too.
  const ids = [
    feedId,
    ...editor.getCurrentPageShapes().filter(isLayerShape).map((s) => s.id),
  ];
  const pageBounds = Box.Common(
    compact(ids.map((id) => editor.getShapePageBounds(id))),
  );
  const viewport = editor.getViewportScreenBounds();
  const maxVisible = viewport.height * 2;

  pageBounds.height = Math.min(maxVisible, pageBounds.height);

  if (accountForSidebar) {
    pageBounds.width += 520;
  }
  // On mobile we want it to zoom more.
  else {
    pageBounds.width -= 70;
    pageBounds.x += 35;
  }

  editor.zoomToBounds(pageBounds);
}

export const defaultFeedVersion = {
  order: "new",
  manifest: {
    filter: {
      and: [
        {
          regex_any: ["text", ["cats", "dogs"], true],
        },
      ],
    },
  },
} as unknown as FeedVersion;

export const defaultCustomNodeFeedVersion = {
  order: "new",
  manifest: {
    filter: {
      metadata: {
        color: "purple",
      },
      and: [
        {
          regex_any: ["text", ["cats", "dogs"], true],
        },
      ],
    },
  },
} as unknown as FeedVersion;
